import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { isAgent } from '../../../constants/roles';
import { ONBOARDING_PATH } from './steps';
import useAgentReadiness from './useAgentReadiness';

/** Whole-route loading state while the setup is checked (design.md §5.5). */
export const GateSpinner = () => (
  <Box
    role="status"
    aria-label="Checking your setup"
    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
  >
    <CircularProgress />
  </Box>
);

/**
 * AgentReadyGate - the agent dashboard and tools open only after onboarding.
 *
 * Someone who signed up as an agent but has no agent role yet goes straight to
 * /agent/onboarding. An agent goes there too until agent.onboarding.get says
 * can_enter_dashboard (Agent ID confirmed and the flow finished); otherwise the
 * children render in place, so the URL and its query (?listing=) are kept.
 * When the check itself fails, or answers with nothing usable (no object, no
 * can_enter_dashboard), the children render: the flow is guidance, and the
 * dashboard has its own error states. The spinner shows only while the check runs.
 */
const usable = (onboarding) =>
  Boolean(onboarding) && typeof onboarding === 'object' && typeof onboarding.can_enter_dashboard === 'boolean';

const AgentReadyGate = ({ children }) => {
  const { currentUser } = useAuth();
  const hasRole = isAgent(currentUser);
  const { onboarding, loading } = useAgentReadiness(hasRole);

  if (!hasRole) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }
  if (usable(onboarding)) {
    return onboarding.can_enter_dashboard ? children : <Navigate to={ONBOARDING_PATH} replace />;
  }
  if (loading) {
    return <GateSpinner />;
  }
  return children;
};

export default AgentReadyGate;
